<template>
  <div v-if="totalSteps > 0" ref="wizardRef" class="form-wizard">
    <!-- Progress -->
    <div class="form-wizard__header">
      <div class="form-wizard__header__title">
        <h2>Apply for this job</h2>
        <div>Step {{ currentStep + 1 }} of {{ totalSteps }}</div>
      </div>
      <div
        :aria-valuenow="progressPercent"
        aria-valuemax="100"
        aria-valuemin="0"
        class="progress form-wizard__progress"
        role="progressbar"
      >
        <div :style="{ width: progressPercent + '%' }" class="progress-bar"></div>
      </div>
    </div>

    <!-- Step content -->
    <form class="form-wizard__container" novalidate @submit.prevent="onNext">
      <div class="form-wizard__container__header">
        <h3 class="form-wizard__container__title">{{ activeStep.label }}</h3>
        <div class="form-wizard__container__subtitle">*indicates a required field</div>
        <div v-if="activeStep.description" class="form-wizard__container__description">
          <div v-html="activeStep.description"></div>
        </div>
      </div>

      <div class="row form-wizard__container__form-group">
        <template v-for="field in activeStep.fields" :key="field.name">
          <div :class="field.name" class="col-12">
            <!-- Label -->
            <label v-if="field.uiLabel !== ''" :for="field.name" class="form-label"
              >{{ field.uiLabel ?? labelFor(field.name)
              }}<span v-if="isQuestionRequired(field)" class="text-danger">*</span></label
            >

            <!-- Text input -->
            <input
              v-if="field.type === 'input_text'"
              :id="field.name"
              v-model.trim="formData[field.name]"
              :class="{ 'is-invalid': showErrors && isFieldInvalid(field) }"
              :required="isExplicitlyRequired(field)"
              class="form-control"
              type="text"
            />

            <!-- Textarea -->
            <textarea
              v-else-if="field.type === 'textarea'"
              :id="field.name"
              v-model.trim="formData[field.name]"
              :class="{ 'is-invalid': showErrors && isFieldInvalid(field) }"
              :required="isExplicitlyRequired(field)"
              class="form-control"
              rows="4"
            ></textarea>

            <!-- Select -->
            <select
              v-else-if="field.type === 'multi_value_single_select'"
              :id="field.name"
              v-model="formData[field.name]"
              :class="{ 'is-invalid': showErrors && isFieldInvalid(field) }"
              :required="isExplicitlyRequired(field)"
              class="form-select"
            >
              <option disabled value="">Select...</option>
              <option v-for="opt in field.values" :key="asKey(opt)" :value="asValue(opt)">
                {{ asLabel(opt) }}
              </option>
            </select>

            <!-- Checkbox -->
            <div v-else-if="field.type === 'multi_value_multi_select'" class="form-check">
              <input
                :id="field.name"
                v-model="formData[field.name]"
                :class="{ 'is-invalid': showErrors && isFieldInvalid(field) }"
                :required="isExplicitlyRequired(field)"
                class="form-check-input"
                type="checkbox"
              />
              <label :for="field.name" class="form-check-label">
                {{ field.uiLabel ?? labelFor(field.name) }}
              </label>
            </div>

            <!-- File input -->
            <template v-else-if="field.type === 'input_file'">
              <CloudDriveFileUpload
                v-model:file="formData[field.name]"
                :class="{ 'is-invalid': showErrors && isFieldInvalid(field) }"
                :required="isExplicitlyRequired(field)"
              />
            </template>

            <!-- Education Repeater -->
            <template v-else-if="field.type === 'education_repeater'">
              <EducationRepeater
                v-model="formData[field.name]"
                :degrees="(field.values as any)?.degrees || []"
                :disciplines="(field.values as any)?.disciplines || []"
                :invalid="showErrors && isFieldInvalid(field)"
                :required="isQuestionRequired(field)"
                :schools="(field.values as any)?.schools || []"
              />
            </template>

            <!-- Fallback to text input -->
            <input
              v-else
              :id="field.name"
              v-model.trim="formData[field.name]"
              :class="{ 'is-invalid': showErrors && isFieldInvalid(field) }"
              :required="isExplicitlyRequired(field)"
              class="form-control"
              type="text"
            />

            <!-- Invalid Text -->
            <div class="invalid-feedback">This field is required.</div>
          </div>
        </template>
      </div>

      <!-- Navigation -->
      <div class="form-wizard__container__btn-group">
        <button
          v-show="currentStep > 0"
          :disabled="submitting"
          class="btn btn-bordered btn-outlined-black"
          type="button"
          @click="prev"
          >Previous</button
        >
        <div class="ms-auto d-flex gap-2">
          <button
            v-if="!isLastStep"
            :disabled="submitting"
            class="btn btn-manatt-black"
            type="submit"
          >
            Next
          </button>
          <button
            v-else
            :disabled="submitting"
            class="btn btn btn-manatt-black"
            type="button"
            @click="onSubmit"
          >
            <span
              v-if="submitting"
              aria-hidden="true"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            {{ submitting ? 'Submitting…' : 'Submit' }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, reactive, ref, toRaw, watch, watchEffect } from 'vue'
import CloudDriveFileUpload from '../ad-hocs/CloudDriveFileUpload.vue'
import EducationRepeater from './EducationRepeater.vue'
import { type CloudFile, useCloudPicker } from '~/composables/useCloudPicker'

type FieldOption = string | number | { label: string; value: string | number }

interface StepField {
  name: string
  type: string
  values?: FieldOption[]
  accept?: string
  multiple?: boolean
  // Optional per-field required; if omitted, falls back to step.required
  required?: boolean
  // Display label for this field, sourced from the parent question's label
  uiLabel?: string
  // Grouping metadata: ties multiple fields to their parent question
  parentKey?: string
  // Indicates the parent question is required (any-of across its fields)
  parentRequired?: boolean
}

interface WizardStep {
  description?: string | null
  label: string
  required?: boolean
  fields: StepField[]
}

// Optional higher-level grouping where each group becomes a single step
interface QuestionGroup {
  title: string
  description?: string | null
  // Each question has the same shape as WizardStep (label/description/required/fields)
  questions: WizardStep[]
}

const props = withDefaults(
  defineProps<{
    // Each group maps to a step
    groups?: QuestionGroup[]
    modelValue?: Record<string, any>
    // Hide/exclude specific fields by their field name
    excludeFields?: string[]
    // When true, disables navigation and shows loading state on submit
    submitting?: boolean
  }>(),
  {}
)
const emit = defineEmits<{
  (e: 'update:modelValue', v: Record<string, any>): void
  (e: 'submit', v: Record<string, any>): void
}>()

const currentStep = ref(0)
const showErrors = ref(false)
const formData = reactive<Record<string, any>>({})
const wizardRef = ref<HTMLElement>()

const { downloadDropboxFile, downloadGoogleDriveFile } = useCloudPicker()

watchEffect(() => {
  if (props.modelValue) {
    for (const [k, v] of Object.entries(props.modelValue)) formData[k] = v
  }
})

// Build steps only from groups (each group is a step)
const computedSteps = computed<WizardStep[]>(() => {
  const groupSrc = props.groups || []
  const exclude = new Set((props.excludeFields || []).filter(Boolean))
  return groupSrc.map((g, idx) => {
    const fields: StepField[] = (g.questions || []).flatMap((q, qIdx) => {
      return (q.fields || [])
        .filter((f) => !exclude.has(f?.name))
        .map((f) => ({
          ...f,
          // Do NOT inherit question.required to individual fields; track at parent level for any-of logic
          required: f.required,
          uiLabel: normalizeLabel(q.label),
          parentKey: `${idx}_${qIdx}_${q.label ?? ''}`,
          parentRequired: Boolean(q.required)
        }))
    })
    return {
      label: g.title || `Step ${idx + 1}`,
      description: g.description ?? null,
      required: undefined,
      fields
    }
  })
})

const activeStep = computed(() => computedSteps.value[currentStep.value])
const stepsLen = computed(() => computedSteps.value.length)
const isLastStep = computed(() => stepsLen.value > 0 && currentStep.value === stepsLen.value - 1)
const progressPercent = computed(() => {
  const len = stepsLen.value
  if (len === 0) return 0
  return Math.round(((currentStep.value + 1) / len) * 100)
})

const totalSteps = computed(() => stepsLen.value)

// Keep currentStep within bounds when steps change dynamically
watch(
  () => stepsLen.value,
  (len) => {
    if (len === 0) {
      currentStep.value = 0
      return
    }
    if (currentStep.value > len - 1) currentStep.value = len - 1
    if (currentStep.value < 0) currentStep.value = 0
  }
)

function labelFor(name: string) {
  // Convert snake_case or camelCase to Title Case if no explicit label provided
  const withSpaces = name.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
}

function normalizeLabel(label: string | undefined) {
  if (!label) return label as any
  if (label === 'VeteranStatus') return 'Veteran Status'
  if (label === 'DisabilityStatus') return 'Disability Status'
  return label
}

function isExplicitlyRequired(field: StepField) {
  // Only the field's own required flag; do not inherit from question here
  return field.required === true
}

function isQuestionRequired(field: StepField) {
  // For UI indicator: either the field itself is required or its parent question is required (any-of)
  return field.required === true || field.parentRequired === true
}

// True once the applicant has put anything at all into an education entry,
// including a school name typed but not yet picked from the autocomplete.
function isEducationEntryTouched(entry: any) {
  return Boolean(
    entry?.school_name_id ||
    entry?.school_name_text ||
    entry?.degree_id ||
    entry?.discipline_id ||
    entry?.start_date?.month ||
    entry?.start_date?.year ||
    entry?.end_date?.month ||
    entry?.end_date?.year
  )
}

// school_name_id is mandatory: the Greenhouse API only accepts a school selected from
// its autocomplete, so free text alone does not make an entry submittable.
function isEducationEntryComplete(entry: any) {
  return Boolean(
    entry?.school_name_id &&
    entry?.degree_id &&
    entry?.discipline_id &&
    entry?.start_date?.month &&
    entry?.start_date?.year &&
    entry?.end_date?.month &&
    entry?.end_date?.year
  )
}

// A started-but-unfinished entry blocks the step even when education is optional,
// otherwise the entry would be silently dropped at submission time.
function hasIncompleteEducationEntry(field: StepField) {
  if (field.type !== 'education_repeater') return false
  const entries = formData[field.name]
  if (!Array.isArray(entries)) return false
  return entries.some((e: any) => isEducationEntryTouched(e) && !isEducationEntryComplete(e))
}

function hasValue(field: StepField) {
  const v = formData[field.name]
  // Education repeater: check if at least one entry is fully filled
  if (field.type === 'education_repeater' && Array.isArray(v)) {
    return v.some(isEducationEntryComplete)
  }
  // File input: consider selected file(s) as value
  if (typeof File !== 'undefined' && v instanceof File) return true
  if (typeof FileList !== 'undefined' && v instanceof FileList) return v.length > 0
  if (Array.isArray(v) && v.length !== undefined) return v.length > 0
  if (typeof v === 'boolean') return v
  return v !== undefined && v !== null && String(v).trim() !== ''
}

function stepValid(step: WizardStep) {
  // Education entries must be finished once started, regardless of required-ness.
  if (step.fields.some(hasIncompleteEducationEntry)) return false

  // Group fields by parent question and apply validation rules:
  // - If any field in a group has field.required === true, each such field must have a value.
  // - Else if the parent question is required, at least one field in the group must have a value.
  // - Otherwise, the group imposes no requirement.
  const groups = new Map<string, StepField[]>()
  for (const f of step.fields) {
    const key = f.parentKey || f.name
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(f)
  }

  for (const [, arr] of groups) {
    const hasExplicitRequired = arr.some((f) => f.required === true)
    if (hasExplicitRequired) {
      // All explicitly required fields must be filled
      if (arr.some((f) => f.required === true && !hasValue(f))) return false
      continue
    }
    const parentReq = arr.some((f) => f.parentRequired === true)
    if (parentReq) {
      // Any-of across fields in the required question
      const anyValue = arr.some((f) => hasValue(f))
      if (!anyValue) return false
    }
  }
  return true
}

function isFieldInvalid(field: StepField) {
  // For invalid styling on individual inputs
  if (hasIncompleteEducationEntry(field)) return true
  if (isExplicitlyRequired(field)) return !hasValue(field)
  if (field.parentRequired) {
    const step = activeStep.value
    if (!step) return false
    const anyValue = step.fields.some((f) => f.parentKey === field.parentKey && hasValue(f))
    return !anyValue
  }
  return false
}

function scrollToWizard() {
  nextTick(() => {
    if (wizardRef.value) {
      const top = wizardRef.value.offsetTop - 66
      window.scrollTo({ top, behavior: 'smooth' })
    }
  })
}

function next() {
  if (currentStep.value < computedSteps.value.length - 1) {
    currentStep.value++
    scrollToWizard()
  }
}

function prev() {
  showErrors.value = false
  if (currentStep.value > 0) {
    currentStep.value--
    scrollToWizard()
  }
}

function slugify(text: string) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_') // Replace spaces with _
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '_') // Replace multiple - with single _
}

function onNext() {
  showErrors.value = true
  if (stepValid(activeStep.value)) {
    showErrors.value = false

    // Track progression
    if (typeof window !== 'undefined' && (window as any).gtag) {
      const eventName = `job_applicant_form_step_${slugify(activeStep.value.label)}`

      ;(window as any).gtag('event', eventName, {
        step_number: currentStep.value + 1,
        step_label: activeStep.value.label,
        total_steps: totalSteps.value,
        action: 'next'
      })
    }

    next()
    emit('update:modelValue', { ...formData })
  }
}

async function onSubmit() {
  showErrors.value = true
  if (!stepValid(activeStep.value)) return
  if (props.submitting) return

  // Track submission
  if (typeof window !== 'undefined' && (window as any).gtag) {
    ;(window as any).gtag('event', 'job_applicant_form_submit', {
      step_number: currentStep.value + 1,
      step_label: activeStep.value.label,
      total_steps: totalSteps.value,
      action: 'submit'
    })
  }

  const payload = await normalizeFilesBeforeSubmit({ ...formData })
  emit('submit', { ...payload })
}

async function normalizeFilesBeforeSubmit(data: Record<string, any>) {
  const result: Record<string, any> = {}

  for (const [key, value] of Object.entries(data)) {
    if (isCloudFile(value)) {
      result[key] = await resolveFile(value)
    } else if (Array.isArray(value) && value.every(isCloudFile)) {
      result[key] = await Promise.all(value.map(resolveFile))
    } else {
      result[key] = value
    }
  }

  return result
}

function isCloudFile(v: any): v is CloudFile {
  const value = toRaw(v)

  return value && typeof value === 'object' && 'provider' in value && 'id' in value
}

async function resolveFile(file: CloudFile): Promise<File | Blob> {
  if (file.provider === 'local') {
    return file.file
  }

  if (file.provider === 'dropbox') {
    return downloadDropboxFile(file)
  }

  if (file.provider === 'google') {
    return downloadGoogleDriveFile(file)
  }

  throw new Error('Unsupported file provider')
}

function asKey(opt: FieldOption) {
  return typeof opt === 'object' ? opt.value : opt
}
function asValue(opt: FieldOption) {
  return typeof opt === 'object' ? opt.value : opt
}
function asLabel(opt: FieldOption) {
  return typeof opt === 'object' ? opt.label : String(opt)
}
</script>

<style lang="scss">
.form-wizard {
  .form-select option {
    color: #212529;
  }
}
</style>
